import React, { useEffect, useMemo, useState } from 'react';

import WavingHandUrl from '@pkg/assets/images/Waving_Hand.png';
import { Checkbox } from '@pkg/components';
import { DepButton, DepInput, DepInputSize } from '@pkg/components/deprecated';
import { useAppDispatch } from '@pkg/hooks';
import { TwoFactorAuthType } from '@pkg/types';

import { type AuthErrorItem } from '@app/client/common/structs';
import { callLogin } from '@app/client/resources/w3auth';
import { createAccount, loginWithToken, setAuthTokens } from '@app/store/actions/auth';

import { Typography } from '../DesignSystem';

import { AuthNewLocalStorage } from './enums';

import { Container, EmojiImage, ErrorMessage, FormGroup, FormRow, PrimaryCopySection, Section } from './styles';

export function SignUpWithEmail() {
    const [checked, setChecked] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [repassword, setRePassword] = useState('');
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();

    const [hasAuthError, setHasAuthError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    async function handleSignUp() {
        try {
            setLoading(true);
            const signUpResponse = await dispatch(
                createAccount(
                    email,
                    password,
                    async () => {
                        const token = await callLogin({ email, password });
                        const twoFAShouldPrompt =
                            (token?.requires_2fa && token?.requires_2fa !== TwoFactorAuthType.Invalid) || false;
                        localStorage.setItem(AuthNewLocalStorage.OAuthLoggedIn, '1');
                        if (twoFAShouldPrompt) {
                            dispatch(setAuthTokens(token));
                        } else {
                            dispatch(loginWithToken(token));
                        }
                        setLoading(false);
                    },
                    false,
                ),
            );

            if (!signUpResponse.ok) {
                setLoading(false);
                if (signUpResponse.fields) {
                    const firstError: AuthErrorItem = Object.values(signUpResponse.fields)[0][0];
                    setErrorMessage(firstError.message);
                    setHasAuthError(true);
                }
            }
        } catch {
            setLoading(false);
            setHasAuthError(true);
        }
    }

    const signUpDisabled = useMemo(() => {
        return !checked || password !== repassword || hasAuthError;
    }, [checked, password, repassword, hasAuthError]);

    useEffect(() => {
        if (hasAuthError) {
            setHasAuthError(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, password]);

    useEffect(() => {
        if (password !== '' && repassword !== '' && password !== repassword) {
            setHasAuthError(true);
            setErrorMessage('Passwords do not match.');
        } else {
            setHasAuthError(false);
        }
    }, [password, repassword]);

    return (
        <Container>
            <Section>
                <EmojiImage src={WavingHandUrl} />
            </Section>
            <PrimaryCopySection>
                <Typography type="headline" size={40}>
                    Sign up with email
                </Typography>
                <Typography type="paragraph" size={18} faded={true}>
                    Use an email and password to access your Yat account.
                </Typography>
            </PrimaryCopySection>
            <Section>
                <FormGroup>
                    <FormRow>
                        <DepInput
                            label="Email"
                            sizeVariant={DepInputSize.Compact}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </FormRow>
                    <FormRow>
                        <DepInput
                            label="Password"
                            type="password"
                            sizeVariant={DepInputSize.Compact}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoFocus={false}
                        />
                    </FormRow>
                    <FormRow>
                        <DepInput
                            label="Retype Password"
                            type="password"
                            sizeVariant={DepInputSize.Compact}
                            value={repassword}
                            autoFocus={false}
                            onChange={(e) => setRePassword(e.target.value)}
                        />
                    </FormRow>
                    <FormRow>
                        <Checkbox
                            id="checkbox-optin"
                            onChange={() => {
                                setChecked((c) => !c);
                            }}
                            checked={checked}
                            wrapperClass="waitlist-email__optin-check"
                        >
                            <Typography type="paragraph">Sign up for 🖖 updates. We won&apos;t spam.</Typography>
                        </Checkbox>
                    </FormRow>
                    <FormRow>
                        <DepButton variant="glow" disabled={signUpDisabled} onClick={handleSignUp} loading={loading}>
                            Sign Up
                        </DepButton>
                    </FormRow>
                </FormGroup>

                <FormGroup>
                    <ErrorMessage>{hasAuthError && errorMessage}</ErrorMessage>
                </FormGroup>
            </Section>
        </Container>
    );
}
